import {
  Box,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { getAllPics } from "../../services/gallery.service";
// import { ModalComponent, useModal } from '../../ui-components/Modal';
import { getUserInfo } from "../../services/auth.service";
import { createBio, getBio, updateBio } from "../../services/bio.service";
import Dropzone from "../../ui-components/Dropzone";
import { useToast } from "../../ui-components/ToastProvider";

export default function Gallery() {
  const [gallery, setGallery] = React.useState([]);
  const [bio, setBio] = React.useState("");
  const [bios, setBios] = React.useState([]);
  const { showToast } = useToast();
  // const { open, handleClose, handleOpen } = useModal();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const cols = isXs ? 1 : isMd ? 2 : 4;

  const [pendingUploads, setPendingUploads] = React.useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = React.useState<number[]>([]);
  const [uploadErrors, setUploadErrors] = React.useState<string[]>([]);
  // drag handled inside Dropzone
  const allowedTypes = /^(image\/.*|video\/.*)$/;
  const maxSizeMB = 20;
  const bioMaxChars = 600;

  React.useEffect(() => {
    getAllPics().then((x) => {
      setGallery(x);
    });
  }, []);

  React.useEffect(() => {
    getBio().then((x) => {
      setBios(x);
    });
  }, []);

  React.useEffect(() => {
    bios.length > 0 && setBio(bios[0].bio);
    console.log(bios);
  }, [bios]);

  // const handleFileAdd = () => {
  //   setTimeout(() => {handleClose()}, 1000);
  //   setTimeout(() => {getAllPics().then(x => {
  //     setGallery(x)
  //   })}, 3000);
  // }

  const addFiles = (files: FileList | File[]) => {
    const asArray: File[] = Array.isArray(files) ? files : Array.from(files);
    const valid: File[] = [];
    const errors: string[] = [];
    asArray.forEach((file) => {
      if (!allowedTypes.test(file.type)) {
        errors.push(`${file.name}: tipo não permitido`);
      } else if (file.size > maxSizeMB * 1024 * 1024) {
        errors.push(`${file.name}: maior que ${maxSizeMB}MB`);
      } else {
        valid.push(file);
      }
    });
    if (valid.length) {
      setPendingUploads(valid);
      setUploadProgress(valid.map(() => 0));
      uploadPendingFilesWithProgress(valid);
    }
    if (errors.length) setUploadErrors((prev) => [...prev, ...errors]);
  };

  const uploadPendingFilesWithProgress = async (filesParam?: File[]) => {
    const files = filesParam ?? pendingUploads;
    if (!files.length) return;
    try {
      const userId = getUserInfo().id;
      await Promise.all(
        files.map(
          (file, idx) =>
            new Promise<void>((resolve, reject) => {
              const formData = new FormData();
              formData.append(String(userId), file);
              const xhr = new XMLHttpRequest();
              xhr.open("POST", `${process.env.REACT_APP_API_URL}/gallery`);
              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  const percent = Math.round((e.loaded / e.total) * 100);
                  setUploadProgress((prev) => {
                    const arr = [...prev];
                    arr[idx] = percent;
                    return arr;
                  });
                }
              };
              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new Error("Falha no upload"));
              };
              xhr.onerror = () => reject(new Error("Falha no upload"));
              xhr.send(formData);
            })
        )
      );
      const updated = await getAllPics();
      setGallery(updated);
      setPendingUploads([]);
      setUploadProgress([]);
      setUploadErrors([]);
      // Recarrega a página ao final do processo de upload
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      console.error(err);
      showToast("Falha ao enviar arquivos. Tente novamente.", {
        severity: "error",
      });
    }
  };

  const handleSaveBio = () => {
    const selectedBio = bios[0];

    const data = {
      bio,
      user_id: +getUserInfo().id,
    };

    if (selectedBio) {
      console.log("alterar");
      updateBio(data, selectedBio.id).then(() =>
        showToast("Bio alterada com sucesso!", { severity: "success" })
      );
    } else {
      console.log("criar");
      createBio(data).then(() =>
        showToast("Bio criada com sucesso!", { severity: "success" })
      );
    }
  };

  return (
    <Grid container spacing={3} my={3}>
      <Grid item xs={12}>
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "0 2px 10px rgba(36,50,77,0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              Galeria
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            Use esta galeria para adicionar fotos que serão usadas para criação
            das suas postagens.
          </Typography>

          <Dropzone
            onAddFiles={(files) => addFiles(files)}
            accept="image/*,video/*"
            title="Arraste e solte imagens ou vídeos"
            subtitle={`ou clique para selecionar. Tamanho máximo por arquivo: ${maxSizeMB}MB.`}
            buttonLabel="Selecionar arquivos"
          />

          {Boolean(uploadErrors.length) && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}
              >
                Alguns arquivos foram ignorados:
              </Typography>
              {uploadErrors.map((err, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  sx={{ color: "#ef4444", display: "block" }}
                >
                  • {err}
                </Typography>
              ))}
            </Box>
          )}

          {Boolean(pendingUploads.length) && (
            <Box sx={{ mt: 2 }}>
              {pendingUploads.map((file, idx) => (
                <Box key={idx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      bgcolor: theme.palette.background.paper,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.primary }}
                      >
                        {file.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {(file.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress[idx] || 0}
                    sx={{ mb: 1 }}
                  />
                </Box>
              ))}
            </Box>
          )}

          <ImageList cols={cols} gap={12} sx={{ marginTop: 2 }}>
            {gallery &&
              gallery
                .filter(
                  (x) =>
                    x.user_id ===
                    getUserInfo().id
                )
                .map((item) => (
                  <ImageListItem
                    key={item.url}
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: "0 1px 4px rgba(36,50,77,0.08)",
                    }}
                  >
                    {item?.file_type?.startsWith("video/") ||
                    /\.(mp4|webm|ogg)$/i.test(item?.url || "") ? (
                      <video
                        src={`${item.url}`}
                        controls
                        muted
                        playsInline
                        style={{
                          width: "100%",
                          height: "420",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <img
                        src={`${item.url}`}
                        alt={item.title}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "420",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </ImageListItem>
                ))}
          </ImageList>
        </Paper>
        {/* <Paper
          sx={{
            p: 2,
            mt: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "0 2px 10px rgba(36,50,77,0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              Bio
            </Typography>
            <Button
              onClick={handleSaveBio}
              variant="contained"
              color="primary"
              sx={{ textTransform: "none", fontWeight: 600 }}
              disabled={bio.trim() === (bios[0]?.bio || "")}
            >
              Salvar
            </Button>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            Esta seção é para você contar sua história e o que você oferece aos
            clientes.
          </Typography>
          <TextField
            id="bio-text"
            label=""
            multiline
            minRows={12}
            fullWidth
            variant="outlined"
            placeholder="Escreva sua bio..."
            onChange={(e) => setBio(e.target.value)}
            value={bio}
            inputProps={{ maxLength: bioMaxChars }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              Dica: conte sua história e o que você oferece.
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              {bio.length}/{bioMaxChars}
            </Typography>
          </Box>
        </Paper> */}
      </Grid>
    </Grid>
  );
}
